import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiX, FiAlertCircle, FiUploadCloud, FiCheckCircle, FiPlay } from 'react-icons/fi';
import { IoCloudUploadOutline } from 'react-icons/io5';

export default function UploadModal({ isOpen, onClose, onUpload, progressLogs = [], currentProgress = null, isProcessing = false }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setUploading(false);
      setUploadProgress(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    if (files.length === 0) return;

    const file = files[0];
    const isZip = file.name.toLowerCase().endsWith('.zip');

    if (!isZip) {
      alert('Por favor, selecione apenas arquivos ZIP');
      return;
    }

    setSelectedFile(file);
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    // Verificar se arquivo não está vazio
    console.log('[UploadModal] Arquivo selecionado:', {
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type
    });

    if (selectedFile.size === 0) {
      alert('Erro: O arquivo está vazio');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Simular progresso de upload
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    // Chamar callback de upload
    try {
      console.log('[UploadModal] Iniciando upload do arquivo...');
      await onUpload(selectedFile);
      console.log('[UploadModal] Upload concluído com sucesso');
      clearInterval(interval);
      setUploadProgress(100);
      // Não fechar mais automaticamente - aguardar processamento
    } catch (error) {
      console.error('[UploadModal] Erro no upload:', error);
      setUploading(false);
      clearInterval(interval);
      alert('Erro ao enviar arquivo: ' + error.message);
    }
  };

  const handleClose = () => {
    // Permitir fechar durante processamento, apenas bloquear durante upload
    if (!uploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const showUploadArea = !uploading && uploadProgress < 100;
  const showProcessing = uploading && uploadProgress === 100;

  // Converter progressLogs para formato do UnifiedTaskProgress (invertido - última primeiro)
  const formattedLogs = progressLogs.map(log => {
    const timestamp = log.split(' - ')[0] || new Date().toLocaleTimeString();
    let message = log.split(' - ').slice(1).join(' - ') || log;
    
    // Remover emojis da mensagem
    message = message.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim();
    
    // Detectar tipo baseado em palavras-chave
    let type = 'info';
    if (message.includes('Erro') || message.includes('Falha') || message.includes('erro')) {
      type = 'error';
    } else if (message.includes('concluí') || message.includes('sucesso') || message.includes('Concluí')) {
      type = 'success';
    } else if (message.includes('Extraindo') || message.includes('Analisando') || message.includes('Processando')) {
      type = 'progress';
    }
    
    return {
      message,
      timestamp,
      type,
      progress: currentProgress?.percentual
    };
  }).reverse(); // Inverter para mostrar última mensagem primeiro

  // Mapear KPIs corretamente do currentProgress
  const taskStats = [
    { label: 'Total XMLs', value: currentProgress?.totalXmls || 0, type: 'info' },
    { label: 'Conformes', value: currentProgress?.conformes || 0, type: 'success' },
    { label: 'Parciais', value: currentProgress?.parciais || 0, type: 'warning' },
    { label: 'Não Conformes', value: currentProgress?.naoConformes || 0, type: 'error' }
  ];

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="title-row">
              <FiUploadCloud />
              <h2>Análise de Conformidade</h2>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleClose} disabled={uploading} title="Fechar">
              <FiX />
            </button>
          </div>
        </ModalHeader>

        <ModalBody>
          {showUploadArea && (
            <UploadSection>
              <DropArea
                $isDragging={isDragging}
                $hasFile={!!selectedFile}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                {!selectedFile ? (
                  <>
                    <DropIcon>
                      <IoCloudUploadOutline />
                    </DropIcon>
                    <DropText>
                      <h4>{isDragging ? 'Solte o arquivo aqui...' : 'Arraste arquivo ZIP aqui'}</h4>
                      <p>ou clique para selecionar</p>
                    </DropText>
                  </>
                ) : (
                  <>
                    <DropIcon className="success">
                      <FiCheckCircle />
                    </DropIcon>
                    <DropText>
                      <h4>{selectedFile.name}</h4>
                      <p>{formatFileSize(selectedFile.size)}</p>
                    </DropText>
                    <RemoveButton onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>
                      <FiX />
                    </RemoveButton>
                  </>
                )}
              </DropArea>

              <InfoBox>
                <FiAlertCircle />
                <div>
                  <p>Faça upload de um arquivo ZIP contendo os XMLs (NF-e/NFC-e) para análise.</p>
                  <span>Campos verificados: IS, CBS e IBS</span>
                </div>
              </InfoBox>
            </UploadSection>
          )}

          {showProcessing && (
            <ProcessingSection>
              <ProcessingGrid>
                {/* Painel de KPIs */}
                <KPIsPanel>
                  <PanelHeader>
                    <PanelLabel>ESTATÍSTICAS</PanelLabel>
                    <PanelBadge>{Math.round(currentProgress?.progress || 0)}%</PanelBadge>
                  </PanelHeader>
                  <KPIsGrid>
                    {taskStats.map((stat, i) => (
                      <KPIItem key={i}>
                        <KPIValue className={stat.type}>{stat.value}</KPIValue>
                        <KPILabel>{stat.label}</KPILabel>
                      </KPIItem>
                    ))}
                  </KPIsGrid>
                  <ProgressBarContainer>
                    <ProgressBarFill $progress={currentProgress?.progress || 0} />
                  </ProgressBarContainer>
                  <ProgressStage>{currentProgress?.stageName || 'Processando...'}</ProgressStage>
                </KPIsPanel>

                {/* Painel de Logs */}
                <LogsPanel>
                  <PanelHeader>
                    <PanelLabel>TERMINAL</PanelLabel>
                    <LogCounter>{formattedLogs.length} linhas</LogCounter>
                  </PanelHeader>
                  <LogsEditor>
                    {formattedLogs.length === 0 ? (
                      <EmptyLogs>
                        <span>$</span> aguardando saída...
                      </EmptyLogs>
                    ) : (
                      formattedLogs.map((log, index) => (
                        <LogLine key={index} $type={log.type}>
                          <LogLineNumber>{formattedLogs.length - index}</LogLineNumber>
                          <LogTimestamp>{log.timestamp}</LogTimestamp>
                          <LogText $type={log.type}>{log.message}</LogText>
                        </LogLine>
                      ))
                    )}
                  </LogsEditor>
                </LogsPanel>
              </ProcessingGrid>
            </ProcessingSection>
          )}
        </ModalBody>

        <ModalFooter $hidden={showProcessing && isProcessing}>
          {showUploadArea && (
            <>
              <FooterButton className="cancel" onClick={handleClose}>
                Cancelar
              </FooterButton>
              <FooterButton className="primary" disabled={!selectedFile} onClick={handleUploadClick}>
                <FiPlay />
                Iniciar Análise
              </FooterButton>
            </>
          )}
          {showProcessing && !isProcessing && (
            <FooterButton className="primary" onClick={handleClose}>
              Concluir
            </FooterButton>
          )}
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}

// ============= STYLED COMPONENTS (Revisor Pattern) =============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 16px;
  width: 100%;
  max-width: 550px;
  max-height: 85vh;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    max-width: 95%;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  min-height: 2.5em;
  padding: 0;
  gap: 0;

  .header-left {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    padding: 0.6em 1em;
    margin-top: 5px;
    background: transparent;
    white-space: nowrap;

    .title-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;

      svg {
        color: ${({ theme }) => theme.text};
        font-size: 0.9em;
        opacity: 0.4;
        flex-shrink: 0;
      }

      h2 {
        margin: 0;
        font-size: 0.85em;
        font-weight: 400;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
        line-height: 1;
        letter-spacing: 0;
        white-space: nowrap;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.6em 1em;
      background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'};
      border: none;
      border-top-right-radius: 12px;
      border-bottom-left-radius: 8px;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 1em;

      &:hover:not(:disabled) {
        opacity: 0.8;
        background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'};
      }

      &:active:not(:disabled) {
        transform: scale(0.98);
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const ModalFooter = styled.div`
  display: ${({ $hidden }) => $hidden ? 'none' : 'flex'};
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid ${({ theme }) => theme.border};
  flex-shrink: 0;
`;

const FooterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 12px;
  border: none;
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', system-ui, sans-serif;

  svg {
    font-size: 1.1em;
  }

  &.cancel {
    background: transparent;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;

    &:hover {
      opacity: 1;
      background: ${({ theme }) => theme.contrast};
    }
  }

  &.primary {
    background: #10b981;
    color: white;

    &:hover:not(:disabled) {
      background: #059669;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

/* ========== Upload Section ========== */

const UploadSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 16px;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 3px;
  }
`;

const DropArea = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 24px;
  min-height: 200px;
  flex: 1;
  background: ${({ $isDragging }) => 
    $isDragging 
      ? 'rgba(16, 185, 129, 0.05)'
      : 'transparent'
  };
  border: 2px dashed ${({ $isDragging, $hasFile, theme }) => 
    $isDragging ? '#10b981' : 
    $hasFile ? 'rgba(16, 185, 129, 0.3)' :
    theme.border
  };
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ $isDragging }) => $isDragging ? '#10b981' : 'rgba(59, 130, 246, 0.3)'};
    background: ${({ $isDragging }) => $isDragging ? 'rgba(16, 185, 129, 0.05)' : 'rgba(59, 130, 246, 0.02)'};
  }
`;

const DropIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    font-size: 1.3em;
    color: #3b82f6;
  }

  &.success {
    background: rgba(16, 185, 129, 0.1);

    svg {
      color: #10b981;
    }
  }
`;

const DropText = styled.div`
  text-align: left;
  line-height: 1.4;
  max-width: 280px;

  h4 {
    margin: 0 0 4px 0;
    font-size: 0.9em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    opacity: 0.8;
  }

  p {
    margin: 0;
    font-size: 0.8em;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
  }
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    opacity: 1;
  }

  svg {
    font-size: 1em;
  }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(59, 130, 246, 0.04)' : 'rgba(59, 130, 246, 0.06)'};
  border: 1px solid ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)'};
  border-radius: 12px;
  flex-shrink: 0;

  > svg {
    font-size: 1em;
    color: #3b82f6;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;

    p {
      margin: 0;
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      line-height: 1.5;
    }

    span {
      font-size: 0.72em;
      color: #3b82f6;
      font-weight: 500;
      opacity: 0.8;
    }
  }
`;

/* ========== Processing Section ========== */

const ProcessingSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const ProcessingGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? '#fafafa' : 'rgba(255,255,255,0.03)'};
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const PanelLabel = styled.span`
  font-size: 0.65em;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  text-transform: uppercase;
`;

const PanelBadge = styled.span`
  font-size: 0.6em;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  letter-spacing: 0.5px;
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
`;

const KPIsPanel = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const KPIsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  padding: 12px 16px;
`;

const KPIItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
`;

const KPIValue = styled.span`
  font-size: 1.3em;
  font-weight: 700;
  color: ${({ theme }) => theme.text};

  &.success { color: #10b981; }
  &.warning { color: #f59e0b; }
  &.error { color: #ef4444; }
  &.info { color: #3b82f6; }
`;

const KPILabel = styled.span`
  font-size: 0.65em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'};
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  transition: width 0.3s ease;
`;

const ProgressStage = styled.div`
  padding: 8px 16px;
  font-size: 0.75em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-align: center;
`;

/* ========== Logs Panel ========== */

const LogsPanel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const LogCounter = styled.span`
  font-size: 0.6em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
`;

const LogsEditor = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => theme.body === '#fafbfc' ? '#ffffff' : 'rgba(0,0,0,0.2)'};
  padding: 0;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', 'Courier New', monospace;
  font-size: 0.72em;
  line-height: 1.6;
  min-height: 0;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 3px;
    &:hover { background: ${({ theme }) => theme.text}40; }
  }
`;

const EmptyLogs = styled.div`
  padding: 24px 16px;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 1em;

  span {
    color: #10b981;
    margin-right: 6px;
  }
`;

const LogLine = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 2px 0;
  border-bottom: 1px solid ${({ theme }) => theme.body === '#fafbfc' ? '#f9fafb' : 'rgba(255,255,255,0.03)'};

  &:hover {
    background: ${({ theme }) => theme.body === '#fafbfc' ? '#f8fafc' : 'rgba(255,255,255,0.02)'};
  }
`;

const LogLineNumber = styled.span`
  width: 36px;
  text-align: right;
  padding: 0 8px;
  color: ${({ theme }) => theme.text};
  opacity: 0.25;
  flex-shrink: 0;
  user-select: none;
  border-right: 1px solid ${({ theme }) => theme.border};
`;

const LogTimestamp = styled.span`
  padding: 0 8px;
  color: ${({ theme }) => theme.text};
  opacity: 0.35;
  flex-shrink: 0;
`;

const LogText = styled.span`
  flex: 1;
  padding-right: 12px;
  word-break: break-word;
  color: ${({ $type }) => 
    $type === 'success' ? '#059669' : 
    $type === 'error' ? '#dc2626' : 
    $type === 'warning' ? '#d97706' : 
    'inherit'
  };
  opacity: ${({ $type }) => $type === 'info' ? 0.7 : 1};
`;
